import React from 'react';
import './Skills.css';

const Skills = () => {
  const teachingSkills = [
    {
      category: "Marketing Management",
      subjects: ["Marketing Management", "Consumer Behavior", "Advertising & Sales Promotion", "Integrated Marketing Communications", "Service Marketing"],
      icon: "📈",
      level: 95,
      color: "#2196F3"
    },
    {
      category: "Human Resources",
      subjects: ["Human Resource Management", "Training & Development", "Industrial Relations & Labor Laws"],
      icon: "👥",
      level: 90,
      color: "#FF9800"
    },
    {
      category: "Operations & Analytics",
      subjects: ["Operations Research", "Business Analytics", "Strategic Management"],
      icon: "📊",
      level: 85,
      color: "#4CAF50"
    }
  ];

  const technicalSkills = [
    {
      name: "MS Office Suite",
      level: 90,
      icon: "💼",
      description: "Advanced proficiency in Word, Excel, PowerPoint"
    },
    {
      name: "Research Methodology",
      level: 92,
      icon: "🔬",
      description: "Qualitative and quantitative research techniques"
    },
    {
      name: "Data Analysis",
      level: 80,
      icon: "📐",
      description: "Statistical analysis and data interpretation"
    },
    {
      name: "Academic Writing",
      level: 95,
      icon: "✍️",
      description: "Research papers, journals, and academic publications"
    },
    {
      name: "Tally & DTP",
      level: 75,
      icon: "💻",
      description: "Basic computer applications and desktop publishing"
    },
    {
      name: "English Typing",
      level: 85,
      icon: "⌨️",
      description: "25 WPM typing speed with accuracy"
    }
  ];

  const softSkills = [
    {
      name: "Teaching & Mentoring",
      level: 95,
      icon: "🎓",
      description: "8+ years of teaching experience across multiple institutions"
    },
    {
      name: "Research Guidance",
      level: 90,
      icon: "📚",
      description: "Mentoring students in research projects and publications"
    },
    {
      name: "Academic Administration",
      level: 88,
      icon: "⚙️",
      description: "Exam coordination, timetable management, departmental roles"
    },
    {
      name: "Curriculum Development",
      level: 85,
      icon: "📖",
      description: "Designing and updating course curriculum"
    },
    {
      name: "Student Assessment",
      level: 92,
      icon: "📝",
      description: "Evaluation and feedback methodologies"
    },
    {
      name: "Leadership",
      level: 87,
      icon: "👨‍💼",
      description: "Department head experience and team management"
    }
  ];

  const certifications = [
    {
      name: "Faculty Development Programs",
      count: "10+",
      description: "ATAL Academy, International FDPs, Research Ethics",
      icon: "🏆"
    },
    {
      name: "AI Integration in Education",
      count: "Multiple",
      description: "Training in AI applications for teaching and assessment",
      icon: "🤖"
    },
    {
      name: "Research Ethics",
      count: "Certified",
      description: "International FDP on Research Ethics in Higher Education",
      icon: "⚖️"
    },
    {
      name: "Entrepreneurship",
      count: "Trained",
      description: "Entrepreneurship and Innovations training program",
      icon: "💡"
    }
  ];

  const ProgressBar = ({ percentage, color }) => (
    <div className="progress-bar">
      <div 
        className="progress-fill" 
        style={{ 
          width: `${percentage}%`,
          background: color
        }}
      >
        <span className="progress-text">{percentage}%</span>
      </div>
    </div>
  );

  const SkillCard = ({ skill, type = "technical" }) => (
    <div className="skill-card">
      <div className="skill-header">
        <div className="skill-icon">{skill.icon}</div>
        <div className="skill-info">
          <h4 className="skill-name">{skill.name || skill.category}</h4>
          <p className="skill-description">{skill.description}</p>
        </div>
        <div className="skill-percentage">{skill.level}%</div>
      </div>
      <ProgressBar percentage={skill.level} color={skill.color} />
      {skill.subjects && (
        <div className="subjects-list">
          {skill.subjects.map((subject, idx) => (
            <span key={idx} className="subject-tag">{subject}</span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <section id="skills" className="skills-section">
      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <h2 className="section-title">Skills & Expertise</h2>
          <div className="section-divider"></div>
          <p className="section-subtitle">
            Comprehensive expertise in teaching, research, and academic administration developed over 8+ years
          </p>
        </div>

        {/* Teaching Expertise */}
        <div className="skills-category">
          <div className="category-header">
            <h3 className="category-title">Teaching Expertise</h3>
            <div className="category-icon">🎯</div>
          </div>
          <div className="skills-grid teaching-grid">
            {teachingSkills.map((skill, index) => (
              <div key={index} className="teaching-card">
                <div className="teaching-header">
                  <div className="subject-icon" style={{ backgroundColor: skill.color }}>
                    {skill.icon}
                  </div>
                  <div className="teaching-info">
                    <h4 className="subject-category">{skill.category}</h4>
                    <ProgressBar percentage={skill.level} color={skill.color} />
                  </div>
                </div>
                <div className="subjects-taught">
                  {skill.subjects.map((subject, idx) => (
                    <div key={idx} className="subject-item">
                      <span className="subject-bullet" style={{ backgroundColor: skill.color }}></span>
                      <span className="subject-name">{subject}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Technical & Soft Skills Grid */}
        <div className="skills-columns">
          {/* Technical Skills */}
          <div className="skills-column">
            <div className="column-header">
              <h3 className="column-title">Technical Skills</h3>
              <div className="column-icon">💻</div>
            </div>
            <div className="skills-list">
              {technicalSkills.map((skill, index) => (
                <SkillCard key={index} skill={skill} type="technical" />
              ))}
            </div>
          </div>

          {/* Soft Skills */}
          <div className="skills-column">
            <div className="column-header">
              <h3 className="column-title">Professional Skills</h3>
              <div className="column-icon">🌟</div>
            </div>
            <div className="skills-list">
              {softSkills.map((skill, index) => (
                <SkillCard key={index} skill={skill} type="soft" />
              ))}
            </div>
          </div>
        </div>

        {/* Certifications & Training */}
        <div className="certifications-section">
          <div className="category-header">
            <h3 className="category-title">Certifications & Training</h3>
            <div className="category-icon">📜</div>
          </div>
          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <div key={index} className="certification-card">
                <div className="cert-icon">{cert.icon}</div>
                <div className="cert-content">
                  <div className="cert-header">
                    <h4 className="cert-name">{cert.name}</h4>
                    <span className="cert-count">{cert.count}</span>
                  </div>
                  <p className="cert-description">{cert.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Skills Summary */}
        <div className="skills-summary">
          <div className="summary-card">
            <div className="summary-icon">🎓</div>
            <div className="summary-content">
              <div className="summary-number">8+</div>
              <div className="summary-label">Years Teaching Experience</div>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📚</div>
            <div className="summary-content">
              <div className="summary-number">15+</div>
              <div className="summary-label">Subjects Taught</div>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">🏆</div>
            <div className="summary-content">
              <div className="summary-number">10+</div>
              <div className="summary-label">FDPs Attended</div>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">🔬</div>
            <div className="summary-content">
              <div className="summary-number">6+</div>
              <div className="summary-label">Research Areas</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Skills;